import React, { useMemo, useState, useEffect } from "react"
import objectPath from "object-path"
import { SvgIcon } from "@material-ui/core"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useHtmlClassService } from "../_core/MetronicLayout"
// Import Layout components
import { Header } from "./header/Header"
import { HeaderMobile } from "./header-mobile/HeaderMobile"
import { Footer } from "./footer/Footer"
import { LayoutInit } from "./LayoutInit"
import { QuickPanel } from "./extras/offcanvas/QuickPanel"
import { QuickUser } from "./extras/offcanvas/QuickUser"
import { ScrollTop } from "./extras/ScrollTop"
import Sidebar from "./Sidebar"
import { shallowEqual, useSelector } from "react-redux"

export function Layout({ children }) {
	const uiService = useHtmlClassService()
	const matches = useMediaQuery("(min-width:992px)")

	const { selectedSite, userClaims, permissions, pageOptions } = useSelector(
		state => ({
			selectedSite: state.profile?.currentSite,
			userClaims: state.auth?.claims,
			permissions: state.auth?.permissions?.permissions,
			pageOptions: state.profile?.currentSite?.options?.pages
		}),
		shallowEqual
	)

	const layoutProps = useMemo(() => {
		return {
			layoutConfig: uiService.config,
			selfLayout: objectPath.get(uiService.config, "self.layout"),
			asideDisplay: objectPath.get(uiService.config, "aside.self.display"),
			subheaderDisplay: objectPath.get(uiService.config, "subheader.display"),
			desktopHeaderDisplay: objectPath.get(uiService.config, "header.self.fixed.desktop"),
			contentCssClasses: uiService.getClasses("content", true),
			contentContainerClasses: uiService.getClasses("content_container", true),
			contentExtended: objectPath.get(uiService.config, "content.extended")
		}
	}, [uiService])

	const [hideDrawer, setHideDrawer] = useState(true)

	const handleHideDrawer = () => {
		setHideDrawer(prev => !prev)
	}

	useEffect(() => {
		if (matches) {
			setHideDrawer(true)
			return
		}

		setHideDrawer(false)
	}, [matches])

	const options = [
		permissions &&
			permissions.Dashboard && {
				label: { title: "Dashboard", icon: <HomeIcon />, redirectTo: "/dashboard" },
				nestedOptions: [
					permissions.Dashboard.PeopleAndAssets && {
						title: "People & Assets",
						redirectTo: "/dashboard"
					}
					//will be used in the feature
					// permissions.Dashboard.Sensors && {
					// 	title: "Sensors",
					// 	redirectTo: "/sensors"
					// }
				].filter(Boolean)
			},

		selectedSite &&
			permissions &&
			permissions.Reporting && {
				label: { title: "Reporting", icon: <ReportingIcon />, redirectTo: "/reporting" },
				nestedOptions: [
					permissions &&
						permissions.Reporting.People && {
							title: "People",
							nestedOptions: [
								permissions &&
									permissions.Reporting.People.Summary && {
										title: "Summary",
										redirectTo: "/reporting/people/summary"
									},
								permissions &&
									permissions.Reporting.People.Timesheets && {
										title: "Timesheets",
										redirectTo: "/reporting/people/timesheets"
									}
							].filter(Boolean)
						},
					permissions &&
						permissions.Reporting.Assets && {
							title: "Assets",
							redirectTo: "/reporting/assets"
						}
				].filter(Boolean)
			},

		selectedSite &&
			permissions &&
			permissions.AuditTool && {
				label: { title: "Audit Tool", icon: <AuditIcon />, redirectTo: "/audit-tool" }
			},

		selectedSite &&
			!selectedSite?.siteInfo?.siteFinished &&
			permissions &&
			permissions.Communication && {
				label: {
					title: "Communication",
					icon: <CommunicationIcon />,
					redirectTo: "/communication"
				}
			},

		selectedSite &&
			permissions &&
			permissions.Admin && {
				label: { title: "Admin", icon: <AdminIcon />, redirectTo: "/admin" },
				nestedOptions: [
					permissions &&
						permissions.Admin.CustomerOnboarding && {
							title: "Customer Onboarding",
							redirectTo: "/admin/customer-on-boarding"
						},
					permissions &&
						permissions.Admin.ManageCustomer && {
							title: "Manage Customer",
							nestedOptions: [
								permissions &&
									permissions.Admin.ManageCustomer.Configuration &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Configuration",
										redirectTo: "/admin/manage-customer/configuration"
									},
								permissions &&
									permissions.Admin.ManageCustomer.Sites && {
										title: "Sites",
										redirectTo: "/admin/manage-customer/sites"
									},
								permissions &&
									permissions.Admin.ManageCustomer.PlatformUsers &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Users",
										redirectTo: "/admin/manage-customer/users"
									},
								permissions &&
									permissions.Admin.ManageCustomer.People &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "People",
										redirectTo: "/admin/manage-customer/people"
									},
								permissions &&
									permissions.Admin.ManageCustomer.Assets &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Assets",
										redirectTo: "/admin/manage-customer/assets"
									}
							].filter(Boolean)
						},
					permissions &&
						permissions.Admin.ManageSite &&
						!selectedSite?.siteInfo?.siteFinished && {
							title: "Manage Site",
							redirectTo: "/admin/manage-site"
						},
					permissions &&
						permissions.Admin.DesignStudio &&
						!selectedSite?.siteInfo?.siteFinished && {
							title: "Design Studio",
							redirectTo: "/admin/design-studio"
						}
				].filter(Boolean)
			},

		//for making sure customer onboarding is avalilable when there are no customers
		!selectedSite &&
			userClaims?.profileLvl >= 4 &&
			permissions &&
			permissions.Admin && {
				label: { title: "Admin", icon: <AdminIcon />, redirectTo: "/admin" },
				nestedOptions: [
					permissions &&
						permissions.Admin.CustomerOnboarding && {
							title: "Customer Onboarding",
							redirectTo: "/admin/customer-on-boarding"
						}
				].filter(Boolean)
			},
		//will be used in the feature
		//selectedSite && {
		// 	label: {
		// 		title: "Rules Engine",
		// 		icon: <RulesIcon />,
		// 		redirectTo: "/rules-engine"
		// 	}
		// },
		selectedSite &&
			permissions &&
			permissions.Operations && {
				label: { title: "Operations", icon: <OperationIcon />, redirectTo: "/operations" },
				nestedOptions: [
					permissions.Operations.Inventory && {
						title: "Inventory",
						redirectTo: "/operations/inventory"
					},
					permissions.Operations.MapInfrastructure && {
						title: "Manage Infrasctructure",
						redirectTo: "/operations/manage-infrastructure"
					}
				].filter(Boolean)
			}
	].filter(Boolean)

	return layoutProps.selfLayout !== "blank" ? (
		<>
			{/*begin::Main*/}
			<HeaderMobile handleHideDrawer={handleHideDrawer} />
			<div className="d-flex flex-column flex-root">
				{/*begin::Page*/}
				<div className="d-flex flex-row flex-column-fluid page">
					{/* {layoutProps.asideDisplay && <Aside />} */}
					<Sidebar
						options={options}
						hideDrawer={hideDrawer}
						handleHideDrawer={handleHideDrawer}
						matches={matches}
					/>
					{/*begin::Wrapper*/}
					<div className="d-flex flex-column flex-row-fluid wrapper" id="kt_wrapper">
						<Header />
						{/*begin::Content*/}
						<div
							id="kt_content"
							className={`content ${layoutProps.contentCssClasses} d-flex flex-column flex-column-fluid`}
						>
							{/* {layoutProps.subheaderDisplay && <SubHeader/>} */}
							{/*begin::Entry*/}
							{!layoutProps.contentExtended && (
								<div className="d-flex flex-column-fluid">
									{/*begin::Container*/}
									<div className={layoutProps.contentContainerClasses}>{children}</div>
									{/*end::Container*/}
								</div>
							)}

							{layoutProps.contentExtended && { children }}
							{/*end::Entry*/}
						</div>
						{/*end::Content*/}
						<Footer />
					</div>
					{/*end::Wrapper*/}
				</div>
				{/*end::Page*/}
			</div>
			<QuickUser />
			<QuickPanel />
			<ScrollTop />
			{/*end::Main*/}
			<LayoutInit />
		</>
	) : (
		// BLANK LAYOUT
		<div className="d-flex flex-column flex-root">{children}</div>
	)
}

function HomeIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				width="19"
				height="20"
				viewBox="0 0 19 20"
				fill="#8C8CA1"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g id="home">
					<path
						id="Vector"
						d="M9.86355 2.30159C9.75816 2.21935 9.62831 2.17468 9.49463 2.17468C9.36094 2.17468 9.23109 2.21935 9.1257 2.30159L0.59375 8.95492L1.3316 9.88811L2.375 9.07456V16.4246C2.37564 16.7394 2.50096 17.0411 2.72352 17.2636C2.94608 17.4862 3.24775 17.6115 3.5625 17.6121H15.4375C15.7523 17.6116 16.054 17.4863 16.2765 17.2637C16.4991 17.0411 16.6244 16.7394 16.625 16.4246V9.07996L17.6684 9.8934L18.4062 8.96014L9.86355 2.30159ZM10.6875 16.4246H8.3125V11.6746H10.6875V16.4246ZM11.875 16.4246V11.6746C11.8746 11.3598 11.7494 11.058 11.5268 10.8354C11.3042 10.6127 11.0023 10.4875 10.6875 10.4871H8.3125C7.99765 10.4875 7.69579 10.6127 7.47316 10.8353C7.25053 11.0579 7.12531 11.3598 7.125 11.6746V16.4246H3.5625V8.14866L9.5 3.52335L15.4375 8.1549V16.4246H11.875Z"
					/>
				</g>
			</svg>
		</SvgIcon>
	)
}

// will be used in the feature
function SensorsIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="20"
				viewBox="0 0 20 20"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M19.0312 8.98943H17.8244C17.8225 7.38956 17.1861 5.85575 16.0548 4.72447C14.9236 3.59319 13.3897 2.95681 11.7899 2.95494V1.74805C13.7098 1.75014 15.5504 2.51374 16.908 3.87131C18.2656 5.22888 19.0292 7.06954 19.0312 8.98943ZM16.014 12.6101H18.4278V13.817H16.014V15.6274C16.0136 15.9473 15.8863 16.254 15.66 16.4803C15.4338 16.7065 15.1271 16.8338 14.8071 16.8343H12.9968V19.248H11.7899V16.8343H8.16918V19.248H6.96228V16.8343H5.15194C4.83196 16.8339 4.5252 16.7066 4.29894 16.4804C4.07268 16.2541 3.94541 15.9473 3.94504 15.6274V13.817H1.53125V12.6101H3.94504V8.98943H1.53125V7.78253H3.94504V5.97218C3.94536 5.65219 4.07262 5.3454 4.29889 5.11913C4.52516 4.89287 4.83195 4.76561 5.15194 4.76529H6.96228V2.3515H8.16918V4.76529H10.583V5.97164L5.15194 5.97218V15.6274H14.8071V10.1963H16.014V12.6101ZM6.96228 13.817H12.9968V7.78253H6.96228V13.817ZM11.7899 12.6101H8.16918V8.98943H11.7899V12.6101ZM14.8071 8.98943H16.014C16.0127 7.86953 15.5672 6.79589 14.7753 6.00401C13.9834 5.21212 12.9098 4.76665 11.7899 4.76529V5.97218C12.5898 5.97313 13.3567 6.29132 13.9223 6.85695C14.488 7.42259 14.8062 8.18949 14.8071 8.98943Z"
				/>
			</svg>
		</SvgIcon>
	)
}

function ReportingIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				width="18"
				height="18"
				viewBox="0 0 18 18"
				fill="#8C8CA1"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g id="chart--ring">
					<path
						id="Vector"
						d="M8.70405 0.177673C7.01771 0.177673 5.36923 0.677733 3.96709 1.61462C2.56494 2.5515 1.4721 3.88313 0.826765 5.44111C0.181429 6.99909 0.0125792 8.71345 0.341569 10.3674C0.670559 12.0213 1.48261 13.5406 2.67504 14.733C3.86746 15.9254 5.38671 16.7375 7.04065 17.0665C8.69459 17.3955 10.409 17.2266 11.9669 16.5813C13.5249 15.9359 14.8565 14.8431 15.7934 13.441C16.7303 12.0388 17.2304 10.3903 17.2304 8.70399C17.2278 6.44345 16.3287 4.27623 14.7303 2.67779C13.1318 1.07934 10.9646 0.18022 8.70405 0.177673ZM15.9814 8.09557H12.3034C12.2019 7.48531 11.9467 6.91094 11.5618 6.4266C11.1769 5.94226 10.675 5.56389 10.1035 5.32716C9.53192 5.09043 8.9095 5.00313 8.29487 5.07349C7.68024 5.14386 7.09365 5.36956 6.59038 5.72934L3.98985 3.12881C5.01262 2.26064 6.25498 1.69131 7.58033 1.48342C8.90568 1.27554 10.2627 1.43715 11.5021 1.95048C12.7416 2.46381 13.8155 3.30899 14.6058 4.39308C15.396 5.47717 15.8721 6.75818 15.9815 8.09527L15.9814 8.09557ZM8.70405 11.1401C8.22224 11.1401 7.75125 10.9972 7.35063 10.7295C6.95002 10.4618 6.63778 10.0814 6.4534 9.63624C6.26902 9.1911 6.22077 8.70129 6.31477 8.22873C6.40877 7.75617 6.64078 7.3221 6.98148 6.98141C7.32217 6.64072 7.75624 6.4087 8.22879 6.31471C8.70135 6.22071 9.19117 6.26895 9.6363 6.45333C10.0814 6.63772 10.4619 6.94996 10.7296 7.35057C10.9973 7.75118 11.1401 8.22217 11.1401 8.70399C11.1394 9.34986 10.8825 9.96906 10.4258 10.4258C9.96913 10.8825 9.34992 11.1394 8.70405 11.1401ZM1.39578 8.70399C1.39512 6.97673 2.00946 5.30567 3.12876 3.99015L5.72928 6.59037C5.36963 7.09367 5.14401 7.68023 5.07368 8.29481C5.00335 8.90939 5.09062 9.53176 5.32728 10.1033C5.56394 10.6748 5.94219 11.1767 6.4264 11.5617C6.91062 11.9466 7.48486 12.202 8.09503 12.3037V15.9822C6.26991 15.8281 4.56901 14.9948 3.32857 13.6472C2.08813 12.2996 1.39842 10.5356 1.39578 8.70399ZM9.31307 15.9818V12.3033C10.0598 12.1758 10.7484 11.8196 11.2841 11.284C11.8197 10.7484 12.1759 10.0597 12.3034 9.31301H15.9819C15.836 11.0328 15.0865 12.6455 13.866 13.866C12.6456 15.0864 11.0329 15.8359 9.31307 15.9818Z"
					/>
				</g>
				<defs>
					<clipPath id="clip0_4926_1832">
						<rect width="18" height="18" />
					</clipPath>
				</defs>
			</svg>
		</SvgIcon>
	)
}

function AuditIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M3.75 19.6973H8.75V18.4473H3.75V3.44727H13.75V9.69727H15V3.44727C14.9997 3.11585 14.8678 2.79811 14.6335 2.56377C14.3992 2.32942 14.0814 2.19761 13.75 2.19727H3.75C3.41859 2.19761 3.10085 2.32942 2.8665 2.56377C2.63216 2.79811 2.50035 3.11585 2.5 3.44727V18.4473C2.50035 18.7787 2.63216 19.0964 2.8665 19.3308C3.10085 19.5651 3.41859 19.6969 3.75 19.6973ZM12.5 4.69727H5V5.94727H12.5V4.69727ZM8.75 9.69727H5V10.9473H8.75V9.69727ZM5 7.19727H12.5V8.44727H5V7.19727ZM8.75 15.9473H5V17.1973H8.75V15.9473ZM16.875 16.5723C17.3723 16.5723 17.8492 16.7698 18.2008 17.1214C18.5525 17.4731 18.75 17.95 18.75 18.4473V19.6973H17.5V18.4473C17.5 18.2815 17.4342 18.1225 17.3169 18.0053C17.1997 17.8881 17.0408 17.8223 16.875 17.8223H13.125C12.9592 17.8223 12.8003 17.8881 12.6831 18.0053C12.5658 18.1225 12.5 18.2815 12.5 18.4473V19.6973H11.25V18.4473C11.25 17.95 11.4475 17.4731 11.7992 17.1214C12.1508 16.7698 12.6277 16.5723 13.125 16.5723H16.875ZM15 15.9473C15.4945 15.9473 15.9778 15.8006 16.3889 15.5259C16.8 15.2512 17.1205 14.8608 17.3097 14.404C17.4989 13.9472 17.5484 13.4445 17.452 12.9595C17.3555 12.4746 17.1174 12.0291 16.7678 11.6795C16.4181 11.3299 15.9727 11.0918 15.4877 10.9953C15.0028 10.8988 14.5001 10.9483 14.0433 11.1376C13.5865 11.3268 13.196 11.6472 12.9213 12.0583C12.6466 12.4695 12.5 12.9528 12.5 13.4473C12.5 14.1103 12.7634 14.7462 13.2322 15.215C13.7011 15.6839 14.337 15.9473 15 15.9473ZM15 12.1973C15.2472 12.1973 15.4889 12.2706 15.6945 12.4079C15.9 12.5453 16.0602 12.7405 16.1549 12.9689C16.2495 13.1973 16.2742 13.4487 16.226 13.6911C16.1778 13.9336 16.0587 14.1563 15.8839 14.3312C15.7091 14.506 15.4863 14.625 15.2439 14.6732C15.0014 14.7215 14.7501 14.6967 14.5216 14.6021C14.2932 14.5075 14.098 14.3473 13.9607 14.1417C13.8233 13.9362 13.75 13.6945 13.75 13.4473C13.75 13.1157 13.8817 12.7978 14.1161 12.5634C14.3505 12.329 14.6685 12.1973 15 12.1973Z"
				/>
			</svg>
		</SvgIcon>
	)
}

// will be used in the feature
function IncidentIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M4.72782 12.993L10 19.2109L15.2744 12.9904C15.3023 12.9573 15.4898 12.7108 15.4898 12.7108L15.4908 12.7092C16.391 11.5233 16.8772 10.0748 16.875 8.58594C16.875 6.76257 16.1507 5.01389 14.8614 3.72458C13.5721 2.43527 11.8234 1.71094 10 1.71094C8.17664 1.71094 6.42796 2.43527 5.13865 3.72458C3.84934 5.01389 3.12501 6.76257 3.12501 8.58594C3.1229 10.0754 3.60951 11.5245 4.51019 12.7108C4.51019 12.7108 4.69788 12.9573 4.72782 12.993ZM5.53789 11.9973C5.52003 11.9738 5.508 11.9579 5.50782 11.9578C4.77156 10.988 4.37362 9.80355 4.37501 8.58594C4.37501 7.09409 4.96764 5.66335 6.02253 4.60846C7.07742 3.55357 8.50816 2.96094 10 2.96094C11.4918 2.96094 12.9226 3.55357 13.9775 4.60846C15.0324 5.66335 15.625 7.09409 15.625 8.58594C15.6264 9.80289 15.2288 10.9867 14.493 11.9561C14.4927 11.9566 14.3462 12.1501 14.3188 12.1846L10 17.2784L5.68694 12.1918C5.66321 12.1623 5.58242 12.0559 5.53789 11.9973ZM10.625 4.83594H9.375V10.4609H10.625V4.83594ZM10 14.2109C10.5178 14.2109 10.9375 13.7912 10.9375 13.2734C10.9375 12.7557 10.5178 12.3359 10 12.3359C9.48224 12.3359 9.0625 12.7557 9.0625 13.2734C9.0625 13.7912 9.48224 14.2109 10 14.2109Z"
				/>
			</svg>
		</SvgIcon>
	)
}

function CommunicationIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M11.0875 19.2109L10 18.5859L12.5 14.2109H16.25C16.5815 14.2109 16.8995 14.0792 17.1339 13.8448C17.3683 13.6104 17.5 13.2925 17.5 12.9609V5.46094C17.5 5.12942 17.3683 4.81147 17.1339 4.57705C16.8995 4.34263 16.5815 4.21094 16.25 4.21094H3.75C3.41848 4.21094 3.10054 4.34263 2.86612 4.57705C2.6317 4.81147 2.5 5.12942 2.5 5.46094V12.9609C2.5 13.2925 2.6317 13.6104 2.86612 13.8448C3.10054 14.0792 3.41848 14.2109 3.75 14.2109H9.375V15.4609H3.75C3.08696 15.4609 2.45107 15.1975 1.98223 14.7287C1.51339 14.2599 1.25 13.624 1.25 12.9609V5.46094C1.25 4.7979 1.51339 4.16201 1.98223 3.69317C2.45107 3.22433 3.08696 2.96094 3.75 2.96094H16.25C16.913 2.96094 17.5489 3.22433 18.0178 3.69317C18.4866 4.16201 18.75 4.7979 18.75 5.46094V12.9609C18.75 13.624 18.4866 14.2599 18.0178 14.7287C17.5489 15.1975 16.913 15.4609 16.25 15.4609H13.225L11.0875 19.2109ZM15 6.71094H5V7.96094H15V6.71094ZM5 10.4609H11.25V11.7109H5V10.4609Z"
				/>
			</svg>
		</SvgIcon>
	)
}

// will be used in the feature
function RulesIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M16.84 4.8085L18.3154 7.36337C18.4598 7.6137 18.5131 7.90632 18.4661 8.19147C18.4191 8.47662 18.2748 8.73668 18.0577 8.92744L16.3077 10.4609L15.4836 9.521L17.2322 7.98837L15.7572 5.4335L13.603 6.16225C13.1023 5.74037 12.5325 5.40813 11.9187 5.18019L11.475 2.96094H8.52498L8.08085 5.18019C7.46277 5.40479 6.8887 5.73569 6.38454 6.15794L4.24285 5.4335L2.76717 7.98837L4.4661 9.48162C4.35249 10.1284 4.35397 10.7903 4.47048 11.4366L2.76773 12.9335L4.24273 15.4884L6.39698 14.7596C6.89765 15.1815 7.46745 15.5138 8.08123 15.7417L8.52498 17.9609H9.99998V19.2109H8.52498C8.23596 19.211 7.95586 19.1109 7.73234 18.9277C7.50882 18.7444 7.3557 18.4894 7.29904 18.206L6.98423 16.6319C6.7003 16.493 6.42632 16.3346 6.16423 16.1579L4.64292 16.6725C4.36921 16.7654 4.07177 16.7604 3.80133 16.6584C3.53088 16.5564 3.30419 16.3638 3.15992 16.1134L1.68454 13.5585C1.5401 13.3082 1.48682 13.0156 1.53375 12.7305C1.58069 12.4453 1.72494 12.1852 1.94198 11.9944L3.14235 10.9394C3.13123 10.7814 3.12498 10.6221 3.12498 10.4609C3.12498 10.3265 3.13429 10.1944 3.14362 10.0622C3.14536 10.0374 3.14711 10.0127 3.14879 9.98794L1.94198 8.92744C1.72494 8.73664 1.58069 8.47656 1.53375 8.19141C1.48682 7.90627 1.5401 7.61367 1.68454 7.36337L3.15992 4.8085C3.30419 4.55806 3.53088 4.36543 3.80133 4.26346C4.07177 4.1615 4.36921 4.15652 4.64292 4.24938L6.15504 4.76094C6.42025 4.58489 6.6974 4.42753 6.98448 4.29L7.29929 2.71587C7.35595 2.43251 7.50903 2.17752 7.73249 1.9943C7.95596 1.81108 8.23601 1.71095 8.52498 1.71094H11.475C11.764 1.71089 12.0441 1.811 12.2676 1.99422C12.4911 2.17745 12.6443 2.43247 12.7009 2.71587L13.0157 4.29C13.2996 4.42901 13.5736 4.58737 13.8357 4.76394L15.357 4.24938C15.6307 4.15652 15.9282 4.1615 16.1986 4.26346C16.4691 4.36543 16.6958 4.55806 16.84 4.8085ZM9.99998 12.9609C9.66963 12.9684 9.34118 12.9089 9.03448 12.7859C8.72778 12.6629 8.44919 12.479 8.21553 12.2454C7.98187 12.0117 7.798 11.7331 7.67502 11.4264C7.55203 11.1197 7.49249 10.7913 7.49998 10.4609C7.49249 10.1306 7.55203 9.80214 7.67502 9.49544C7.798 9.18874 7.98187 8.91015 8.21553 8.67649C8.44919 8.44283 8.72778 8.25896 9.03448 8.13598C9.34118 8.01299 9.66963 7.95345 9.99998 7.96094C10.3303 7.95345 10.6588 8.01299 10.9655 8.13598C11.2722 8.25896 11.5508 8.44283 11.7844 8.67649C12.0181 8.91015 12.202 9.18874 12.3249 9.49544C12.4479 9.80214 12.5075 10.1306 12.5 10.4609H13.75C13.75 9.71926 13.53 8.99423 13.118 8.37755C12.7059 7.76087 12.1203 7.28022 11.435 6.99639C10.7498 6.71256 9.99582 6.6383 9.26839 6.78299C8.54096 6.92769 7.87278 7.28484 7.34833 7.80929C6.82388 8.33374 6.46673 9.00192 6.32204 9.72935C6.17734 10.4568 6.2516 11.2108 6.53543 11.896C6.81926 12.5812 7.29991 13.1669 7.91659 13.5789C8.53328 13.991 9.2583 14.2109 9.99998 14.2109V12.9609ZM18.75 12.9609H12.5V14.2109H18.75V12.9609ZM18.75 15.4609H12.5V16.7109H18.75V15.4609ZM12.5 17.9609H18.75V19.2109H12.5V17.9609Z"
				/>
			</svg>
		</SvgIcon>
	)
}

function AdminIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="21"
				height="20"
				viewBox="0 0 21 20"
				fill="#8C8CA1"
			>
				<g clipPath="url(#clip0_13186_137494)">
					<path d="M15.7501 6.98339H14.8751V5.38546C14.8751 3.18031 12.9151 1.39062 10.5001 1.39062C8.08506 1.39062 6.12506 3.18031 6.12506 5.38546H7.87506C7.87506 4.05917 9.04756 2.98856 10.5001 2.98856C11.9526 2.98856 13.1251 4.05917 13.1251 5.38546V6.98339H5.25006C4.28756 6.98339 3.50006 7.70246 3.50006 8.58132V16.571C3.50006 17.4498 4.28756 18.1689 5.25006 18.1689H15.7501C16.7126 18.1689 17.5001 17.4498 17.5001 16.571V8.58132C17.5001 7.70246 16.7126 6.98339 15.7501 6.98339ZM15.7501 16.571H5.25006V8.58132H15.7501V16.571ZM10.5001 14.1741C11.4626 14.1741 12.2501 13.455 12.2501 12.5762C12.2501 11.6973 11.4626 10.9782 10.5001 10.9782C9.53756 10.9782 8.75006 11.6973 8.75006 12.5762C8.75006 13.455 9.53756 14.1741 10.5001 14.1741Z" />
				</g>
				<defs>
					<clipPath id="clip0_13186_137494">
						<rect width="21" height="19.1752" transform="translate(0 0.591797)" />
					</clipPath>
				</defs>
			</svg>
		</SvgIcon>
	)
}

function OperationIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path d="M18.3217 15.4609V14.2109H17.0085C16.9279 13.8197 16.7726 13.4477 16.551 13.1154L17.4823 12.1841L16.5985 11.3004L15.6673 12.2316C15.3349 12.0101 14.9629 11.8547 14.5717 11.7741V10.4609H13.3217V11.7741C12.9304 11.8547 12.5585 12.01 12.2261 12.2316L11.2948 11.3003L10.4111 12.1841L11.3423 13.1153C11.1208 13.4477 10.9654 13.8197 10.8848 14.2109H9.57166V15.4609H10.8848C10.9654 15.8521 11.1208 16.2241 11.3423 16.5565L10.411 17.4878L11.2948 18.3715L12.226 17.4403C12.5584 17.6618 12.9304 17.8171 13.3217 17.8977V19.2109H14.5717V17.8977C14.9629 17.8172 15.3349 17.6618 15.6672 17.4403L16.5985 18.3716L17.4822 17.4878L16.551 16.5566C16.7725 16.2242 16.9279 15.8522 17.0085 15.4609H18.3217ZM13.9467 16.7109C13.5758 16.7109 13.2133 16.601 12.905 16.3949C12.5966 16.1889 12.3563 15.8961 12.2144 15.5535C12.0725 15.2109 12.0353 14.8339 12.1077 14.4701C12.18 14.1064 12.3586 13.7723 12.6208 13.5101C12.8831 13.2479 13.2171 13.0693 13.5809 12.997C13.9446 12.9246 14.3216 12.9617 14.6642 13.1037C15.0068 13.2456 15.2996 13.4859 15.5057 13.7942C15.7117 14.1026 15.8217 14.4651 15.8217 14.8359C15.8211 15.3331 15.6234 15.8096 15.2719 16.1612C14.9204 16.5127 14.4438 16.7104 13.9467 16.7109Z" />
				<path d="M14.9189 3.16992H13.0439V2.54492C13.043 2.2137 12.911 1.89631 12.6768 1.66209C12.4426 1.42788 12.1252 1.29588 11.7939 1.29492H6.79394C6.46272 1.29588 6.14533 1.42788 5.91112 1.66209C5.6769 1.89631 5.5449 2.2137 5.54395 2.54492V3.16992H3.66895C3.33772 3.17088 3.02033 3.30288 2.78612 3.53709C2.5519 3.77131 2.4199 4.0887 2.41895 4.41992V17.5449C2.4199 17.8761 2.5519 18.1935 2.78612 18.4277C3.02033 18.662 3.33772 18.794 3.66895 18.7949H8.04394V17.5449H3.66895V4.41992H5.54395V6.29492H13.0439V4.41992H14.9189V8.16992H16.1689V4.41992C16.168 4.0887 16.036 3.77131 15.8018 3.53709C15.5676 3.30288 15.2502 3.17088 14.9189 3.16992ZM11.7939 5.04492H6.79394V2.54492H11.7939V5.04492Z" />
			</svg>
		</SvgIcon>
	)
}
